import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FILTER_KEYS } from "../utils/filterClinics";
import { DEFAULT_SORT, SORT_ORDER } from "../utils/sortClinics";

const PARAM_PREFIX = "clinicList";
const SEARCH_PARAM = `${PARAM_PREFIX}Search`;
const SORT_PARAM = `${PARAM_PREFIX}Sort`;
const ORDER_PARAM = `${PARAM_PREFIX}Order`;

function filterParam(key) {
  return `${PARAM_PREFIX}${key[0].toUpperCase()}${key.slice(1)}`;
}

/**
 * Every param is prefixed with `clinicList` so it can't collide with other lists' URL state.
 */
export default function useClinicListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  function updateParams(changes) {
    setSearchParams(
      (prev) => {
        Object.entries(changes).forEach(([param, value]) =>
          value ? prev.set(param, value) : prev.delete(param),
        );
        return prev;
      },
      { replace: true },
    );
  }

  const search = searchParams.get(SEARCH_PARAM) ?? "";
  const sort = {
    orderBy: searchParams.get(SORT_PARAM) ?? DEFAULT_SORT.orderBy,
    order: searchParams.get(ORDER_PARAM) ?? DEFAULT_SORT.order,
  };
  const filtersKey = FILTER_KEYS.map((key) => searchParams.get(filterParam(key)) ?? "").join("|");
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((key) => [key, searchParams.get(filterParam(key)) ?? ""])),
    [filtersKey],
  );

  function toggleSort(column) {
    const isSameColumn = sort.orderBy === column;
    const order =
      isSameColumn && sort.order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC;
    const isDefault = column === DEFAULT_SORT.orderBy && order === DEFAULT_SORT.order;
    updateParams({
      [SORT_PARAM]: isDefault ? null : column,
      [ORDER_PARAM]: isDefault ? null : order,
    });
  }

  return {
    search,
    setSearch: (value) => updateParams({ [SEARCH_PARAM]: value }),
    sort,
    toggleSort,
    filters,
    setFilter: (key, value) => updateParams({ [filterParam(key)]: value }),
    clearFilters: () =>
      updateParams(Object.fromEntries(FILTER_KEYS.map((key) => [filterParam(key), null]))),
    viewKey: [search, filtersKey, sort.orderBy, sort.order].join("|"),
  };
}
